import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LoginService } from '../login.service';
import { FlashMessagesService } from 'angular2-flash-messages';

@Component({
  selector: 'app-manage-salons',
  templateUrl: './manage-salons.component.html',
  styleUrls: ['./manage-salons.component.css']
})
export class ManageSalonsComponent implements OnInit {

  currentUser: any = {};
  salons: any[] = [];
  filteredSalons: any[] = [];
  paginatedSalons: any[] = [];
  showLoader: boolean = false;
  disabled: boolean;
  Math = Math;

  // Pagination
  currentPage: number = 1;
  pageSize: number = 25;
  totalPages: number = 0;
  totalRecords: number = 0;

  // Statistics
  totalMain: number = 0;
  totalBranch: number = 0;

  // Filters
  filters = {
    salonName: '',
    location: '',
    type: '',
    status: ''
  };

  constructor(
    private router: Router,
    private loginservice: LoginService,
    private _flashMessagesService: FlashMessagesService
  ) { }

  ngOnInit(): void {
    this.currentUser = this.initializeCurrentUser();
    if (!this.currentUser || Object.keys(this.currentUser).length === 0) {
      this.router.navigate(['/login']);
      return;
    }

    this.checkPermissions();
    this.loadSalons();
  }

  private initializeCurrentUser(): any {
    try {
      const sessionData = this.loginservice && this.loginservice.getLocalSession ? this.loginservice.getLocalSession('currentUser') : null;
      if (sessionData && typeof sessionData === 'string') {
        return JSON.parse(sessionData);
      }
      return {};
    } catch (error) {
      console.error('Error parsing session data:', error);
      return {};
    }
  }

  checkPermissions() {
    // Super admin: full access
    // Salon admin: can edit their own salon and users only
    // Others: no access
    if (this.currentUser.superAdmin == 1) {
      this.disabled = false;
    } else if (this.currentUser.userType == 0) {
      this.disabled = true; // Can't add new salons, but can edit their own
    } else {
      this._flashMessagesService.show('Access denied. Only super admin or salon admin can manage salons.', { cssClass: 'alert-danger' });
      this.disabled = true;
    }
  }

  canEdit(salon: any): boolean {
    if (this.currentUser.superAdmin == 1) {
      return true;
    }
    // if (this.currentUser.userType == 0 && salon.id == this.currentUser.salon_id) {
    if (this.currentUser.userType == 0) {
      return true;
    }
    return false;
  }

  canDelete(salon: any): boolean {
    return this.currentUser.superAdmin == 1;
  }

  canAddNew(): boolean {
    return this.currentUser.superAdmin == 1;
  }

  canSwitchSalon(salon: any): boolean {
    // Only salon admins (userType 0) can switch salons, and not to their current salon
    return this.currentUser.userType == 0 && salon.id != this.currentUser.salon_id;
  }

  switchSalon(salon: any) {
    if (confirm(`Switch to salon "${salon.salonName}"? This will log you into the selected salon.`)) {
      this.showLoader = true;
      this.loginservice.switchSalon(salon.id, this.currentUser).subscribe(
        data => {
          if (data['status'] === 'success') {
            // Clear current localStorage
            localStorage.clear();
            // Set new currentUser data
            localStorage.setItem('currentUser', JSON.stringify(data));
            this._flashMessagesService.show(`Successfully switched to ${salon.salonName}`, { cssClass: 'alert-success' });
            // Reload the page to refresh all components with new salon data
            window.location.reload();
          } else {
            this._flashMessagesService.show(data['message'] || 'Failed to switch salon', { cssClass: 'alert-danger' });
          }
          this.showLoader = false;
        },
        error => {
          alert('Network Error switchSalon API');
          this._flashMessagesService.show('Error switching salon', { cssClass: 'alert-danger' });
          this.showLoader = false;
        }
      );
    }
  }

  loadSalons() {
    this.showLoader = true;
    this.loginservice.getManageableSalons(this.currentUser).subscribe(
      data => {
        if (data && data['salons']) {
          this.salons = data['salons'];
          this.calculateStatistics();
          this.applyFilters();
        } else {
          this.salons = [];
          this.filteredSalons = [];
          this.paginatedSalons = [];
        }
        this.showLoader = false;
      },
      error => {
        alert('Network Error getManageableSalons API');
        this._flashMessagesService.show('Error loading salons', { cssClass: 'alert-danger' });
        this.showLoader = false;
      }
    );
  }

  calculateStatistics() {
    this.totalRecords = this.salons.length;
    this.totalMain = this.salons.filter(s => s.isBranch == 0 || s.isBranch === '0').length;
    this.totalBranch = this.salons.filter(s => s.isBranch == 1 || s.isBranch === '1').length;
  }

  applyFilters() {
    this.filteredSalons = this.salons.filter(salon => {
      return (!this.filters.salonName || salon.salonName.toLowerCase().includes(this.filters.salonName.toLowerCase())) &&
        (!this.filters.location || salon.location.toLowerCase().includes(this.filters.location.toLowerCase())) &&
        (this.filters.type === '' || salon.isBranch == this.filters.type || salon.isBranch === this.filters.type) &&
        (this.filters.status === '' || salon.status == this.filters.status || salon.status === this.filters.status);
    });

    this.totalPages = Math.ceil(this.filteredSalons.length / this.pageSize);
    this.currentPage = 1;
    this.updatePagination();
  }

  clearFilters() {
    this.filters = {
      salonName: '',
      location: '',
      type: '',
      status: ''
    };
    this.applyFilters();
  }

  updatePagination() {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;
    this.paginatedSalons = this.filteredSalons.slice(startIndex, endIndex);
  }

  goToPage(page: number) {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
      this.updatePagination();
    }
  }

  getPageNumbers(): number[] {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(this.totalPages, start + maxVisible - 1);

    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  }

  addSalon() {
    this.router.navigate(['/add-salon']);
  }

  editSalon(salonId: number) {
    this.router.navigate(['/add-salon', salonId]);
  }

  deleteSalon(salonId: number, salonName: string) {
    if (confirm(`Are you sure you want to delete salon "${salonName}"?`)) {
      this.showLoader = true;
      this.loginservice.deleteSalon(salonId, this.currentUser).subscribe(
        data => {
          if (data['status'] === 'success') {
            this._flashMessagesService.show('Salon deleted successfully', { cssClass: 'alert-success' });
            this.loadSalons();
          } else {
            this._flashMessagesService.show(data['message'], { cssClass: 'alert-danger' });
          }
          this.showLoader = false;
        },
        error => {
          alert('Network Error deleteSalon API');
          this._flashMessagesService.show('Error deleting salon', { cssClass: 'alert-danger' });
          this.showLoader = false;
        }
      );
    }
  }

  getStatusText(status: number): string {
    return status === 1 ? 'Active' : 'Inactive';
  }

  getBranchText(isBranch: number): string {
    return isBranch === 1 ? 'Branch' : 'Main';
  }

  exportToExcel() {
    if (this.filteredSalons.length === 0) {
      this._flashMessagesService.show('No data to export', { cssClass: 'alert-warning' });
      return;
    }

    // Prepare data for export
    const exportData = this.filteredSalons.map((salon, index) => {
      return {
        'S.No': index + 1,
        // 'Parent Salon ID': salon.parentSalonId || '',
        'Parent Salon Name': salon.parentSalonName || 'Main Salon',
        'Salon Name': salon.salonName || '',
        'Highlight': salon.highlight || '',
        'Is Branch': salon.isBranch == 1 ? 'Yes' : 'No',
        'Branch Name': salon.branchName || '',
        'Location': salon.location || '',
        'Owner Name': salon.ownerName || '',
        'Logo': salon.logo || '',
        'Contact Phone': salon.cntactPhone || '',
        'Service Percentage': salon.service_percent || '0',
        'Product Percentage': salon.product_percent || '0',
        'Added On': salon.addedOn || '',
        // 'Added By': salon.addedBy || ''
      };
    });

    // Convert to CSV format
    const headers = Object.keys(exportData[0]);
    const csvContent = [
      headers.join(','),
      ...exportData.map(row =>
        headers.map(header => {
          const value = row[header];
          // Escape commas and quotes in values
          return typeof value === 'string' && (value.includes(',') || value.includes('"'))
            ? `"${value.replace(/"/g, '""')}"`
            : value;
        }).join(',')
      )
    ].join('\n');

    // Create and download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);

    // Generate filename with current date
    const currentDate = new Date().toISOString().split('T')[0];
    const filename = `salons_export_${currentDate}.csv`;
    link.setAttribute('download', filename);

    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    this._flashMessagesService.show(`Exported ${exportData.length} salon records to ${filename}`, { cssClass: 'alert-success' });
  }
}


// WEBPACK FOOTER //
// ./src/app/manage-salons/manage-salons.component.ts